/**
 * "Add to list" - a popover of every list the operator has, each with a
 * checkbox for whether this company is already on it, plus a row to create a
 * brand new list (naming it and dropping this company in, in one action).
 *
 * A popover rather than a page: this sits inline in the registry row and the
 * company profile header, and organising a company into lists should not
 * mean leaving the screen the operator is already looking at.
 */

import { useEffect, useRef, useState } from 'react';
import { useLists } from '../lib/lists-context.jsx';
import { IconCheck, IconListPlus, IconPlus } from './Icons.jsx';

export default function AddToListMenu({ companyNumber, className = '' }) {
  const { lists, isPending, addToList, removeFromList, createList } = useLists();
  const [isOpen, setIsOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [error, setError] = useState(null);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onPointerDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) setIsOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen]);

  const memberCount = lists.filter((list) => list.companyNumbers?.includes(companyNumber)).length;

  const handleCreate = async (event) => {
    event.preventDefault();
    const name = newName.trim();
    if (!name) return;
    setError(null);
    try {
      await createList(name, [companyNumber]);
      setNewName('');
      setIsCreating(false);
    } catch (caught) {
      setError(caught.message);
    }
  };

  return (
    <div className={`list-menu ${className}`} ref={rootRef}>
      <button
        type="button"
        className={`btn btn-ghost btn-sm tip tip-below${memberCount > 0 ? ' is-active' : ''}`}
        onClick={(event) => {
          event.stopPropagation();
          setIsOpen((open) => !open);
        }}
        data-tip="Add to a list"
        aria-expanded={isOpen}
      >
        <IconListPlus />
        {memberCount > 0 ? `In ${memberCount} list${memberCount === 1 ? '' : 's'}` : 'Add to list'}
      </button>

      {isOpen ? (
        <div className="list-menu-popover combobox-list" onClick={(event) => event.stopPropagation()}>
          {error ? <div className="list-menu-error">{error}</div> : null}

          {lists.length === 0 ? (
            <div className="list-menu-empty">No lists yet - create one below.</div>
          ) : (
            <ul className="list-menu-options" role="list">
              {lists.map((list) => {
                const inList = list.companyNumbers?.includes(companyNumber) ?? false;
                const pending = isPending(`list:${list.id}:${companyNumber}`);
                return (
                  <li key={list.id}>
                    <label className="checkbox-row list-menu-option">
                      <input
                        type="checkbox"
                        className="checkbox"
                        checked={inList}
                        disabled={pending}
                        onChange={() =>
                          void (inList
                            ? removeFromList(list.id, companyNumber)
                            : addToList(list.id, companyNumber))
                        }
                      />
                      <span className="list-menu-option-name">{list.name}</span>
                      <span className="text-xs text-faint">{list.itemCount}</span>
                    </label>
                  </li>
                );
              })}
            </ul>
          )}

          {isCreating ? (
            <form className="list-menu-new" onSubmit={handleCreate}>
              <input
                type="text"
                className="input"
                placeholder="List name"
                value={newName}
                onChange={(event) => setNewName(event.target.value)}
                maxLength={120}
                autoFocus
              />
              <button type="submit" className="btn btn-primary btn-sm" disabled={!newName.trim()}>
                <IconCheck />
              </button>
            </form>
          ) : (
            <button
              type="button"
              className="btn btn-ghost btn-sm list-menu-new-trigger"
              onClick={() => setIsCreating(true)}
            >
              <IconPlus />
              New list
            </button>
          )}
        </div>
      ) : null}
    </div>
  );
}
